import { useRef, useEffect } from 'react';

export default function TherapistSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Calming nature image */}
      <div className="w-full h-48 md:h-64 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=soft%20colorful%20wildflowers%20meadow%20field%20close%20up%20pink%20purple%20lavender%20white%20flowers%20bokeh%20dreamy%20nature%20no%20people%20no%20artificial%20objects%20peaceful%20healing%20minimal%20photography%20pastel%20gentle%20light%20spring%20bloom&width=1440&height=400&seq=sanctuary-flowers-divider-2&orientation=landscape"
          alt="花の風景"
          className="w-full h-full object-cover object-center"
        />
      </div>

      <section
        className="w-full py-24 px-4"
        style={{ backgroundColor: 'rgba(143, 166, 139, 0.1)' }}
      >
        <div className="max-w-3xl mx-auto">
          <div ref={ref} className="opacity-0 translate-y-8 transition-all duration-1000">
            <p className="text-center font-sans text-[#8FA68B] text-xs tracking-[0.4em] mb-6 uppercase">
              therapist
            </p>
            <h2 className="font-serif text-2xl md:text-3xl text-[#3A3530] text-center mb-16">
              セラピストについて
            </h2>

            <div className="flex flex-col md:flex-row items-start gap-10 md:gap-16">
              {/* Photo area */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div
                  className="w-52 h-64 md:w-56 md:h-72 rounded-lg overflow-hidden"
                  style={{ border: '1px solid rgba(194, 168, 130, 0.4)' }}
                >
                  <img
                    src="https://storage.readdy-site.link/project_files/7ba6f116-45e4-4c55-a245-29d4f9051785/67b3e8d3-7454-4753-ab56-d7d5b8f62eeb_1000001303.jpg?v=f85d8244e48eede12587a23be9708d8b"
                    alt="sanctuary セラピスト 山田美那子"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="mt-4 text-center">
                  <p className="font-serif text-[#3A3530] text-sm">山田 美那子</p>
                  <p className="font-sans text-[#8FA68B] text-xs tracking-widest mt-1">Minako Yamada</p>
                </div>
              </div>

              {/* Story text area */}
              <div className="flex-1">
                <p className="font-serif text-[#3A3530] text-base leading-loose mb-6">
                  sanctuary セラピスト
                </p>

                <div className="space-y-4 font-sans text-[#6B6460] text-sm leading-[2.2]">
                  <p>40代の頃、私は自分が一番嫌だったパターンの中にいました。</p>
                  <p>同じパターンの喧嘩。同じパターンの不安。<br />
                  いつもどこかに「また繰り返している」という感覚がつきまとっていました。</p>
                  <p>いろいろ学びました。実践もしました。<br />
                  人にアドバイスをする側にもなりました。<br />
                  それなのに、自分自身は過去のループから抜け出せていなかった。</p>
                  <p>こんなにやっているのに、なぜ——。<br />
                  その問いが、ずっと頭の中を回っていました。</p>
                  <p>あるとき、自分のセッションを振り返って気づいたことがあります。<br />
                  私はずっと「教える人」をやっていた。<br />
                  答えを持っている側に立って、正しい方向を示そうとしていた。<br />
                  でも、それは私自身が一番苦しかったやり方と同じでした。</p>
                  <p>そこから、手放すことにしました。<br />
                  教える人ではなく、一緒に見つける人になろうと。<br />
                  お互いの限界を超えていくような時間を、セッションの中で一緒に体験したいと。</p>
                  <p>人には誰でも、心の中に静かで神聖な場所があると思っています。<br />
                  でもその場所は、一人では見つけにくい。<br />
                  目に見えないし、自分でもその存在に気づかないまま日々を過ごしていることが多いから。</p>
                  <p>私自身も、何度もその場所を諦めかけました。<br />
                  もういい、と思ったことも一度や二度ではありません。<br />
                  でもその時、私以上にその場所を信じて、<br />
                  そこで待っていてくれた人たちがいました。</p>
                  <p>だから今度は、私がそこで待っていたいと思っています。</p>
                </div>

                <div
                  className="mt-10 pt-8"
                  style={{ borderTop: '1px solid rgba(143, 166, 139, 0.3)' }}
                >
                  <p className="font-serif text-[#3A3530] text-sm leading-loose italic">
                    sanctuary——あなたの心の中にある、あなただけの安全な場所。<br />
                    その場所を一緒に見つけにいきませんか。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
